<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentScoringEntry.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentScoringEntry.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button
            outline
            color="info"
            @click="$router.push({ name: 'student-scoring-entry-create' })"
            >{{ $t('add_new') }}</ts-button
          >
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
        <!-- drawer -->
        <a-drawer
          :title="$t('filter')"
          placement="right"
          :closable="false"
          :visible="visible"
          @close="() => (visible = false)"
          :zIndex="1020"
          width="300px"
        >
          <drawer-filter />
          <div class="row">
            <div class="col-md-12">
              <ts-button
                color="primary"
                class="mr-2"
                @click.prevent="fetchData"
                >{{ $t('view') }}</ts-button
              >
              <ts-button color="danger" @click.prevent="clearFilter">{{
                $t('studentScoringEntry.clear')
              }}</ts-button>
            </div>
          </div>
        </a-drawer>
      </ts-card-wrapper>
      <!-- table list -->
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'cycle_value_text',
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td>{{ record.student_name }}</td>
            <td>{{ record.academic_class.class_name }}</td>
            <td>{{ record.grade_name_foreign }}</td>
            <td>{{ record.scoring_cycle_name }}</td>
            <td>{{ record.main_program }}</td>
            <td>{{ record.term_name }}</td>
            <td>{{ record.ac_year }}</td>
            <td>{{ record.score_type_text }}</td>
            <td class="ts-whitespace-no-wrap text-center">
              <a
                href="#"
                class="text-primary"
                @click.prevent="editScore(record)"
              >
                <i class="fa fa-edit"></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <!-- numeric scoring entry -->
      <a-modal
        v-if="numeric_scoring"
        v-model="numeric_scoring"
        :zIndex="1020"
        :title="entry_on_student"
        :maskClosable="false"
        :footer="null"
        width="700px"
      >
        <numeric-scoring-entry
          v-model="model"
          @closeModal="() => (numeric_scoring = false)"
        />
      </a-modal>
      <!-- grading scoring entry -->
      <a-modal
        v-if="grading_scoring"
        v-model="grading_scoring"
        :zIndex="1020"
        :title="entry_on_student"
        :maskClosable="false"
        :footer="null"
        width="700px"
      >
        <grading-scoring-entry
          v-model="model"
          @closeModal="() => (grading_scoring = false)"
          :params-get-component="paramsGetComponentForGrading"
        />
      </a-modal>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import DrawerFilter from './components/drawer-filter'
import GradingScoringEntry from './components/grading-score-entry'
import NumericScoringEntry from './components/numeric-score-entry'

export default {
  name: 'studentScoringEntry',
  components: {
    DrawerFilter,
    GradingScoringEntry,
    NumericScoringEntry
  },
  data() {
    return {
      numeric_scoring: false,
      grading_scoring: false,
      entry_on_student: null,
      loading: false,
      visible: false,
      paramsGetComponentForGrading: {
        grade_id: 0,
        sc_bas_id: null
      },
      model: {
        class_id: null, // add on param
        score_type: 1, // add on param but no use in DB
        st_class_id: null,
        acp_id: null,
        ac_year_id: null,
        ac_term_id: null,
        student_id: null,
        scoring_cycle_id: null,
        cycle_value: null,
        sc_subject_id: null
      }
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('studentScoringEntry', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.studentScoringEntry.search
      },
      set(newValue) {
        this.$store.commit('studentScoringEntry/SET_SEARCH', newValue)
        this.$store.commit('studentScoringEntry/RESET_CURRENT_PAGE')
      },
    },
    columns() {
      return [
        { name: this.$t('studentScoringEntry.branchName') },
        { name: this.$t('studentScoringEntry.studentName') },
        { name: this.$t('studentScoringEntry.classroom') },
        { name: this.$t('studentScoringEntry.grade') },
        { name: this.$t('studentScoringEntry.scoringCycle') },
        { name: this.$t('studentScoringEntry.mainProgram') },
        { name: this.$t('studentScoringEntry.academicTerm') },
        { name: this.$t('studentScoringEntry.academicYear') },
        { name: this.$t('studentScoringEntry.scoreType') },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    },
  },
  methods: {
    ...mapActions('studentScoringEntry', [
      'getClassOperation',
      'getScoringCycle',
      'getScoringCycleValue',
    ]),
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('studentScoringEntry/fetch', attributes)
        .then((response) => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchClass() {
      this.loading = true
      this.getScoringCycle()
      this.getScoringCycleValue()
      this.getClassOperation()
        .then((response) => {
          this.classes = response.data
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearFilter() {
      this.$store.commit('studentScoringEntry/RESET_FILTER')
      this.fetchData()
    },
    editScore(record) {
      var sc_bas_id = 1 // default is item base
      var scoring_cycle = record.scoring_cycle_id

      this.entry_on_student = record.student_name
      this.model.student_id = record.student_id
      this.model.st_class_id = record.st_class_id
      this.model.class_id = record.class_id
      this.model.acp_id = record.acp_id
      this.model.ac_year_id = record.ac_year_id
      this.model.ac_term_id = record.ac_term_id
      this.model.scoring_cycle_id = record.scoring_cycle_id
      this.model.cycle_value = record.cycle_value
      this.model.sc_subject_id = record.academic_class.subject_id

      if (record.score_type == 1) {
        // entry score for numberic
        if (scoring_cycle == 1) {
          sc_bas_id = record.grade.monthly_score_sc_bas_id
        } else if (scoring_cycle == 2) {
          sc_bas_id = record.grade.semester_score_sc_bas_id
        }

        this.numeric_scoring = true
        this.model.score_type = 1
        this.model.sc_bas_id = sc_bas_id

      } else if (record.score_type == 2) {
        // entry score for grading
        this.paramsGetComponentForGrading.grade_id = record.grade_id

        if (scoring_cycle == 1) {
          sc_bas_id = record.grade.monthly_score_sc_bas_id
        } else if (scoring_cycle == 2) {
          sc_bas_id = record.grade.semester_score_sc_bas_id
        }

        this.paramsGetComponentForGrading.sc_bas_id = sc_bas_id

        this.grading_scoring = true
        this.model.score_type = 2
      }
    },
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('studentScoringEntry/RESET_STATE')
    this.$store.commit('studentScoringEntry/RESET_FILTER')
    next()
  },
}
</script>
